import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Button, 
  Spin, 
  Alert, 
  Descriptions, 
  Space,
  Divider,
  Radio,
  Input,
  message,
  Table,
  Modal,
  Result
} from 'antd'
import { 
  ArrowLeftOutlined,
  CalendarOutlined, 
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  PictureOutlined
} from '@ant-design/icons'
import { Image } from 'antd'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { AppointmentScheduler } from '../components/AppointmentScheduler'
import { AffirmFinancingBlock } from '../components/financing/AffirmFinancingBlock'
import { amountDollarsToCents } from '@serviceflow/shared'
import dayjs from 'dayjs'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

// Helper function to detect and render URLs and images in text
const renderNotesWithMedia = (notes: string) => {
  // Regex to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = notes.split(urlRegex)
  
  // Image file extensions
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      // Reset regex lastIndex
      urlRegex.lastIndex = 0
      
      // Check if it's an image URL
      if (imageExtensions.test(part)) {
        return (
          <div key={index} style={{ marginTop: 12, marginBottom: 12 }}>
            <a href={part} target="_blank" rel="noopener noreferrer">
              <div style={{ 
                display: 'inline-block',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 4,
                background: '#fafafa'
              }}>
                <Image
                  src={part}
                  alt="Reference image"
                  width={150}
                  height={150}
                  style={{ 
                    objectFit: 'cover',
                    borderRadius: 4 
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAEYYNVAoKDNYAAA=="
                  preview={{
                    mask: <Space><PictureOutlined /> View Full Size</Space>
                  }}
                />
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: 11, 
                  color: '#666',
                  marginTop: 4 
                }}>
                  <PictureOutlined /> Reference Image
                </div>
              </div>
            </a>
          </div>
        )
      } else {
        // Regular URL - render as link
        return (
          <a 
            key={index}
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#1890ff',
              wordBreak: 'break-all'
            }}
          >
            <LinkOutlined style={{ marginRight: 4 }} />
            {part}
          </a>
        )
      }
    }
    // Regular text - preserve whitespace
    return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>
  })
}

interface EstimateItem {
  id: string
  title: string
  description?: string
  quantity: number
  unitPrice: number
  totalPrice: number
  optionNumber?: number
}

interface EstimateDetails {
  id: string
  estimateNumber: string
  title: string
  description?: string
  status: string
  hasMultipleOptions: boolean
  
  // Option 1
  option1Title?: string
  option1Description?: string
  option1Subtotal?: number
  option1TaxAmount?: number
  option1Total?: number
  
  // Option 2
  option2Title?: string
  option2Description?: string
  option2Subtotal?: number
  option2TaxAmount?: number
  option2Total?: number
  
  // Option 3
  option3Title?: string
  option3Description?: string
  option3Subtotal?: number
  option3TaxAmount?: number
  option3Total?: number
  
  selectedOption?: number
  validUntil?: string
  createdAt: string
  approvedAt?: string
  jobId?: string | null
  
  depositRequired?: boolean
  depositAmount?: number
  
  customerSignedName?: string
  customerSignedEmail?: string
  customerSignedAt?: string
  additionalNotes?: string
  
  estimateItems: EstimateItem[]
  
  business: {
    id: string
    name: string
    phone?: string
    email?: string
    settings?: any
  }
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    'draft': 'default',
    'pending': 'blue',
    'approved': 'green',
    'rejected': 'red',
    'expired': 'orange'
  }
  return colors[status] || 'default'
}

export const EstimateDetails: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || undefined
  const optionParam = searchParams.get('option')
  const { isAuthenticated, portalConfig } = useCustomerAuth()
  
  const [estimate, setEstimate] = useState<EstimateDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [selectedOption, setSelectedOption] = useState<number | undefined>()
  const [notes, setNotes] = useState('')
  const [declineReason, setDeclineReason] = useState('')
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showScheduler, setShowScheduler] = useState(false)
  const [approvalResult, setApprovalResult] = useState<any>(null)
  const [scheduledJob, setScheduledJob] = useState<any>(null)

  useEffect(() => {
    if (id) {
      loadEstimate(id)
    }
  }, [id])

  const loadEstimate = async (estimateId: string) => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user has access (either authenticated or has token)
      if (!token && !isAuthenticated) {
        setError('You need to be logged in or have a valid access link to view this estimate.')
        setLoading(false)
        return
      }
      
      const estimateData = await customerApi.getEstimate(estimateId, token)
      setEstimate(estimateData)
      
      // Pre-select option from URL parameter if provided
      if (optionParam && [1, 2, 3].includes(parseInt(optionParam))) {
        setSelectedOption(parseInt(optionParam))
      } 
      // Otherwise, pre-select option if already approved
      else if (estimateData.selectedOption) {
        setSelectedOption(estimateData.selectedOption)
      } 
      // Or default to first option if multiple options exist
      else if (estimateData.hasMultipleOptions) {
        setSelectedOption(1)
      }
    } catch (err: any) {
      console.error('Failed to load estimate:', err)
      setError(err.response?.data?.message || 'Failed to load estimate details')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!estimate) return

    try {
      setApproving(true)
      
      const approvalData = {
        selectedOption: estimate.hasMultipleOptions ? selectedOption : undefined,
        notes
      }

      const result = await customerApi.approveEstimate(estimate.id, approvalData, token)
      
      // Store approval result
      setApprovalResult(result)
      
      // Show success message
      if (result.depositRequired && result.invoice) {
        message.success(`Estimate approved! Deposit invoice ${result.invoice.invoiceNumber} has been created.`)
      } else {
        message.success('Estimate approved successfully!')
      }
      
      // Reload estimate to show updated status
      await loadEstimate(estimate.id)
      setShowApprovalModal(false)
      
      // Show appointment scheduler
      setShowScheduler(true)
      
    } catch (err: any) {
      console.error('Failed to approve estimate:', err)
      message.error(err.response?.data?.message || 'Failed to approve estimate')
    } finally {
      setApproving(false)
    }
  }

  const handleDecline = async () => {
    if (!estimate) return

    try {
      setDeclining(true)
      await customerApi.declineEstimate(estimate.id, { reason: declineReason || undefined }, token)
      message.success('Estimate declined')
      await loadEstimate(estimate.id)
      setShowDeclineModal(false)
      setDeclineReason('')
    } catch (err: any) {
      console.error('Failed to decline estimate:', err)
      message.error(err.response?.data?.message || 'Failed to decline estimate')
    } finally {
      setDeclining(false)
    }
  }

  const handleAppointmentScheduled = async (job: any) => {
    setScheduledJob(job)
    setShowScheduler(false)
    message.success('Appointment scheduled successfully!')
    
    // Reload estimate to update jobId
    if (estimate) {
      await loadEstimate(estimate.id)
    }
  }

  const getOptionTotal = (optionNumber: number) => {
    switch (optionNumber) {
      case 1:
        return estimate?.option1Total || 0
      case 2:
        return estimate?.option2Total || 0
      case 3:
        return estimate?.option3Total || 0
      default:
        return 0
    }
  }

  const getOptionSubtotal = (optionNumber: number) => {
    switch (optionNumber) {
      case 1:
        return estimate?.option1Subtotal || 0
      case 2:
        return estimate?.option2Subtotal || 0
      case 3:
        return estimate?.option3Subtotal || 0
      default:
        return 0
    }
  }

  const getOptionTax = (optionNumber: number) => {
    switch (optionNumber) {
      case 1:
        return estimate?.option1TaxAmount || 0
      case 2:
        return estimate?.option2TaxAmount || 0
      case 3:
        return estimate?.option3TaxAmount || 0
      default:
        return 0
    }
  }

  const getOptionTitle = (optionNumber: number) => {
    switch (optionNumber) {
      case 1:
        return estimate?.option1Title || `Option 1`
      case 2:
        return estimate?.option2Title || `Option 2`
      case 3:
        return estimate?.option3Title || `Option 3`
      default:
        return `Option ${optionNumber}`
    }
  }

  const getOptionDescription = (optionNumber: number) => {
    switch (optionNumber) {
      case 1:
        return estimate?.option1Description
      case 2:
        return estimate?.option2Description
      case 3:
        return estimate?.option3Description
      default:
        return undefined
    }
  }

  const renderEstimateItems = (optionNumber?: number) => {
    if (!estimate) return null

    const items = optionNumber 
      ? estimate.estimateItems.filter(item => item.optionNumber === optionNumber)
      : estimate.estimateItems

    const columns = [
      {
        title: 'Item',
        dataIndex: 'title',
        key: 'title',
        render: (text: string, record: EstimateItem) => (
          <div>
            <Text strong>{text}</Text>
            {record.description && (
              <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text></div>
            )}
          </div>
        )
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        align: 'center' as const,
        render: (qty: number) => qty || '-'
      },
      {
        title: 'Unit Price',
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 120,
        align: 'right' as const,
        render: (price: number) => price != null ? `$${Number(price).toFixed(2)}` : '-'
      },
      {
        title: 'Total',
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        width: 120,
        align: 'right' as const,
        render: (price: number) => price != null ? <Text strong>${Number(price).toFixed(2)}</Text> : <Text strong>-</Text>
      }
    ]

    return (
      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        pagination={false}
        size="middle"
        bordered
        style={{ marginTop: '16px' }}
      />
    )
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading estimate details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: token && !isAuthenticated ? '24px' : '0' }}>
        <Alert
          message="Error Loading Estimate"
          description={error}
          type="error"
          showIcon
          action={
            <Space>
              {!token && !isAuthenticated && (
                <Button size="small" type="primary" onClick={() => navigate('/login')}>
                  Login
                </Button>
              )}
              {isAuthenticated && (
                <Button size="small" onClick={() => navigate('/estimates')}>
                  Back to Estimates
                </Button>
              )}
              {(token || isAuthenticated) && (
                <Button size="small" type="primary" onClick={() => id && loadEstimate(id)}>
                  Retry
                </Button>
              )}
            </Space>
          }
        />
      </div>
    )
  }

  if (!estimate) {
    return (
      <div style={{ padding: token && !isAuthenticated ? '24px' : '0' }}>
        <Alert
          message="Estimate Not Found"
          description="The requested estimate could not be found."
          type="warning"
          showIcon
          action={
            isAuthenticated ? (
              <Button onClick={() => navigate('/estimates')}>
                Back to Estimates
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  const isExpired = estimate.validUntil && dayjs(estimate.validUntil).isBefore(dayjs())
  const canApprove = ['sent', 'viewed', 'pending', 'draft'].includes(estimate.status) && !isExpired
  const financingOption = estimate.hasMultipleOptions
    ? (selectedOption ?? estimate.selectedOption ?? 1)
    : 1
  const financingAmountCents = amountDollarsToCents(Number(getOptionTotal(financingOption)))

  return (
    <div style={{ 
      padding: token && !isAuthenticated ? '24px' : '0',
      background: token && !isAuthenticated ? '#f5f5f5' : 'transparent',
      minHeight: token && !isAuthenticated ? '100vh' : 'auto'
    }}>
      {/* Header */}
      <Card 
        style={{ 
          marginBottom: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              {isAuthenticated && (
                <Button 
                  icon={<ArrowLeftOutlined />} 
                  onClick={() => navigate('/estimates')}
                  type="link"
                  style={{ padding: 0 }}
                >
                  Back to Estimates
                </Button>
              )}
              <Space align="center">
                <Title level={2} style={{ margin: 0 }}>
                  {estimate.title}
                </Title>
                <Tag 
                  color={getStatusColor(estimate.status)} 
                  icon={estimate.status === 'approved' ? <CheckCircleOutlined /> : undefined}
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {estimate.status.toUpperCase()}
                </Tag>
              </Space>
            </Space>
          </Col>
          {canApprove && (
            <Col>
              <Space>
                <Button 
                  danger
                  size="large"
                  icon={<CloseCircleOutlined />}
                  onClick={() => setShowDeclineModal(true)}
                  style={{ 
                    height: '48px',
                    fontSize: '16px',
                    borderRadius: '6px'
                  }}
                >
                  Decline
                </Button>
                <Button 
                  type="primary" 
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={() => setShowApprovalModal(true)}
                  style={{ 
                    height: '48px',
                    fontSize: '16px',
                    borderRadius: '6px'
                  }}
                >
                  Approve Estimate
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {canApprove && (
        <AffirmFinancingBlock
          amountCents={financingAmountCents}
          estimateId={estimate.id}
          source="estimate_details"
        />
      )}

      {/* Estimate Disclaimer Notice */}
      <Alert
        message="This is an estimate, not an invoice"
        description="No payment is due at this time."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: '24px' }}
      />

      {/* Expired Warning */}
      {isExpired && estimate.status !== 'approved' && (
        <Alert
          message="This estimate has expired"
          description={`This estimate was valid until ${dayjs(estimate.validUntil).format('MMMM D, YYYY')}. Please contact ${estimate.business.name} to request a new estimate.`}
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Approval Confirmation */}
      {estimate.status === 'approved' && (
        <>
          <Alert
            message="Estimate Approved"
            description={
              <div>
                <p>This estimate was approved on {dayjs(estimate.approvedAt).format('MMMM D, YYYY h:mm A')}.</p>
                {estimate.depositRequired && estimate.depositAmount && (
                  <p><strong>A deposit invoice of ${Number(estimate.depositAmount).toFixed(2)} has been created and sent to you.</strong></p>
                )}
              </div>
            }
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: '24px' }}
          />
          
          {/* Schedule Appointment Button for approved estimates without a job */}
          {!estimate.jobId ? (
            <Card style={{ marginBottom: '24px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Title level={4} style={{ margin: 0 }}>
                  <CalendarOutlined /> Schedule Your Appointment
                </Title>
                <Text type="secondary">
                  Your estimate has been approved! Schedule an appointment to get started with the work.
                </Text>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<CalendarOutlined />}
                  onClick={() => setShowScheduler(true)}
                  block
                >
                  Schedule Appointment
                </Button>
              </Space>
            </Card>
          ) : (
            <Alert
              message="Appointment Scheduled"
              description="An appointment has been scheduled for this estimate. You can view the details in the Jobs section."
              type="info"
              showIcon
              icon={<CalendarOutlined />}
              style={{ marginBottom: '24px' }}
              action={
                isAuthenticated && (
                  <Button size="small" onClick={() => navigate('/jobs')}>
                    View Jobs
                  </Button>
                )
              }
            />
          )}
        </>
      )}

      {/* Declined Confirmation */}
      {estimate.status === 'declined' && (
        <Alert
          message="Estimate Declined"
          description={
            <div>
              <p>This estimate was declined{estimate.declinedAt ? ` on ${dayjs(estimate.declinedAt).format('MMMM D, YYYY h:mm A')}` : ''}.</p>
              <p>If you change your mind, please contact the business to request a new estimate.</p>
            </div>
          }
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          style={{ marginBottom: '24px' }}
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Left Column */}
        <Col xs={24} lg={16}>
          {/* Estimate Information */}
          <Card 
            title={
              <span style={{ fontSize: '18px', fontWeight: 600 }}>
                <FileTextOutlined style={{ marginRight: '8px' }} />
                Estimate Information
              </span>
            }
            style={{ 
              marginBottom: '24px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <Descriptions column={1} labelStyle={{ fontWeight: 500 }}>
              <Descriptions.Item label="Estimate Number">
                <Text strong style={{ fontSize: '16px' }}>{estimate.estimateNumber}</Text>
              </Descriptions.Item>
              
              {estimate.description && (
                <Descriptions.Item label="Description">
                  <Paragraph style={{ marginBottom: 0 }}>{estimate.description}</Paragraph>
                </Descriptions.Item>
              )}
              
              <Descriptions.Item label="Created">
                <Space>
                  <CalendarOutlined style={{ color: '#1890ff' }} />
                  <Text>{dayjs(estimate.createdAt).format('MMMM D, YYYY')}</Text>
                </Space>
              </Descriptions.Item>
              
              {estimate.validUntil && (
                <Descriptions.Item label="Valid Until">
                  <Space>
                    <CalendarOutlined style={{ color: isExpired ? '#ff4d4f' : '#52c41a' }} />
                    <Text type={isExpired ? 'danger' : 'success'}>
                      {dayjs(estimate.validUntil).format('MMMM D, YYYY')}
                    </Text>
                  </Space>
                </Descriptions.Item>
              )}

              {estimate.depositRequired && estimate.depositAmount && (
                <Descriptions.Item label="Deposit Required">
                  <Text strong type="warning" style={{ fontSize: '16px' }}>
                    <DollarOutlined /> ${Number(estimate.depositAmount).toFixed(2)}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Options Selection */}
          {estimate.hasMultipleOptions ? (
            <Card 
              title={
                <span style={{ fontSize: '18px', fontWeight: 600 }}>
                  <InfoCircleOutlined style={{ marginRight: '8px' }} />
                  Estimate Options
                </span>
              }
              style={{ 
                marginBottom: '24px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {[1, 2, 3].map(optionNum => {
                  const optionTotal = getOptionTotal(optionNum)
                  if (!optionTotal) return null

                  const isSelected = estimate.selectedOption === optionNum
                  
                  return (
                    <Card 
                      key={optionNum}
                      type="inner"
                      title={
                        <Space>
                          <Radio 
                            checked={isSelected}
                            disabled={estimate.status === 'approved'}
                          />
                          <span>{getOptionTitle(optionNum)}</span>
                          {isSelected && <Tag color="green">Selected</Tag>}
                        </Space>
                      }
                      style={{ 
                        border: isSelected ? '2px solid #52c41a' : undefined,
                        cursor: estimate.status === 'approved' ? 'default' : 'pointer'
                      }}
                      onClick={() => {
                        if (estimate.status !== 'approved') {
                          setSelectedOption(optionNum)
                        }
                      }}
                    >
                      {getOptionDescription(optionNum) && (
                        <Paragraph type="secondary">{getOptionDescription(optionNum)}</Paragraph>
                      )}
                      
                      {renderEstimateItems(optionNum)}
                      
                      <Divider />
                      
                      <Row justify="end">
                        <Col>
                          <Space direction="vertical" align="end">
                            <Text>Subtotal: ${Number(getOptionSubtotal(optionNum)).toFixed(2)}</Text>
                            <Text>Tax: ${Number(getOptionTax(optionNum)).toFixed(2)}</Text>
                            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                              Total: ${Number(optionTotal).toFixed(2)}
                            </Title>
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  )
                })}
              </Space>
            </Card>
          ) : (
            <>
              {/* Single Option - Items */}
              <Card 
                title={
                  <span style={{ fontSize: '18px', fontWeight: 600 }}>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Estimate Items
                  </span>
                }
                style={{ 
                  marginBottom: '24px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                {renderEstimateItems()}
              </Card>

              {/* Single Option - Total */}
              <Card 
                style={{ 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                }}
              >
                <Row justify="end">
                  <Col>
                    <Space direction="vertical" align="end" size="small">
                      <Text style={{ fontSize: '15px' }}>
                        Subtotal: <strong>${Number(estimate.option1Subtotal || 0).toFixed(2)}</strong>
                      </Text>
                      <Text style={{ fontSize: '15px' }}>
                        Tax: <strong>${Number(estimate.option1TaxAmount || 0).toFixed(2)}</strong>
                      </Text>
                      <Divider style={{ margin: '8px 0' }} />
                      <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                        Total: ${Number(estimate.option1Total || 0).toFixed(2)}
                      </Title>
                    </Space>
                  </Col>
                </Row>
              </Card>
            </>
          )}

          {/* Additional Notes */}
          {estimate.additionalNotes && (
            <Card title="Additional Notes" style={{ marginTop: '24px' }}>
              <div>{renderNotesWithMedia(estimate.additionalNotes)}</div>
            </Card>
          )}
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={8}>
          {/* Business Information */}
          <Card 
            title={
              <span style={{ fontSize: '18px', fontWeight: 600 }}>
                <InfoCircleOutlined style={{ marginRight: '8px' }} />
                Contact Information
              </span>
            }
            style={{ 
              marginBottom: '24px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: '24px'
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong style={{ fontSize: '16px' }}>{estimate.business.name}</Text>
              </div>
              
              {estimate.business.phone && (
                <Button 
                  type="link" 
                  icon={<InfoCircleOutlined />}
                  onClick={() => window.open(`tel:${estimate.business.phone}`)}
                  style={{ padding: 0 }}
                >
                  {estimate.business.phone}
                </Button>
              )}
              
              {estimate.business.email && (
                <Button 
                  type="link" 
                  icon={<InfoCircleOutlined />}
                  onClick={() => window.open(`mailto:${estimate.business.email}`)}
                  style={{ padding: 0 }}
                >
                  {estimate.business.email}
                </Button>
              )}
            </Space>
          </Card>

          {/* Approval Information */}
          {estimate.status === 'approved' && estimate.customerSignedAt && (
            <Card title="Approval Details">
              <Descriptions column={1} size="small">
                {estimate.customerSignedName && (
                  <Descriptions.Item label="Approved By">
                    {estimate.customerSignedName}
                  </Descriptions.Item>
                )}
                
                {estimate.customerSignedEmail && (
                  <Descriptions.Item label="Email">
                    {estimate.customerSignedEmail}
                  </Descriptions.Item>
                )}
                
                <Descriptions.Item label="Date">
                  {dayjs(estimate.customerSignedAt).format('MMM D, YYYY h:mm A')}
                </Descriptions.Item>

                {estimate.hasMultipleOptions && estimate.selectedOption && (
                  <Descriptions.Item label="Selected Option">
                    <Tag color="green">{getOptionTitle(estimate.selectedOption)}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Col>
      </Row>

      {/* Approval Modal */}
      <Modal
        title="Approve Estimate"
        open={showApprovalModal}
        onCancel={() => setShowApprovalModal(false)}
        onOk={handleApprove}
        confirmLoading={approving}
        okText="Approve Estimate"
        okButtonProps={{ 
          size: 'large',
          icon: <CheckCircleOutlined />
        }}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Review Before Approving"
            description="By approving this estimate, you agree to the services and pricing outlined above. This will initiate the next steps in your service request."
            type="info"
            showIcon
          />

          {estimate.hasMultipleOptions && (
            <div>
              <Text strong>Selected Option:</Text>
              <div style={{ marginTop: '8px' }}>
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {getOptionTitle(selectedOption!)} - ${Number(getOptionTotal(selectedOption!)).toFixed(2)}
                </Tag>
              </div>
            </div>
          )}

          {estimate.depositRequired && estimate.depositAmount && (
            <Alert
              message="Deposit Required"
              description={`A deposit of $${Number(estimate.depositAmount).toFixed(2)} will be required. An invoice will be created and sent to you after approval.`}
              type="warning"
              showIcon
            />
          )}

          <div>
            <Text strong>Additional Notes (Optional):</Text>
            <TextArea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional comments or questions..."
              style={{ marginTop: '8px' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Decline Modal */}
      <Modal
        title="Decline Estimate"
        open={showDeclineModal}
        onCancel={() => { setShowDeclineModal(false); setDeclineReason(''); }}
        onOk={handleDecline}
        confirmLoading={declining}
        okText="Decline Estimate"
        okButtonProps={{
          danger: true,
          size: 'large',
          icon: <CloseCircleOutlined />
        }}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Are you sure?"
            description="Declining this estimate will notify the business. You can always request a new estimate if you change your mind."
            type="warning"
            showIcon
          />
          <div>
            <Text strong>Reason for declining (optional):</Text>
            <TextArea
              rows={3}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Let us know why you're declining..."
              style={{ marginTop: '8px' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Appointment Scheduler */}
      {estimate && (
        <AppointmentScheduler
          visible={showScheduler}
          estimateId={estimate.id}
          estimateTitle={estimate.title}
          token={token}
          depositRequired={approvalResult?.depositRequired}
          invoiceNumber={approvalResult?.invoice?.invoiceNumber}
          onClose={() => setShowScheduler(false)}
          onSuccess={handleAppointmentScheduled}
        />
      )}

      {/* Success Screen */}
      {scheduledJob && (
        <Modal
          open={true}
          footer={null}
          onCancel={() => setScheduledJob(null)}
          width={600}
          centered
        >
          <Result
            status="success"
            title="All Set!"
            subTitle={
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Text>Your estimate has been approved and your appointment has been scheduled.</Text>
                </div>
                <Card size="small" style={{ textAlign: 'left' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Job Number:</Text>
                      <br />
                      <Text strong>{scheduledJob.jobNumber}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Scheduled Date:</Text>
                      <br />
                      <Text strong>
                        {dayjs(scheduledJob.scheduledAt).format('MMMM D, YYYY [at] h:mm A')}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary">Technician:</Text>
                      <br />
                      <Text strong>{scheduledJob.technician?.name}</Text>
                    </div>
                  </Space>
                </Card>
                {approvalResult?.depositRequired && (
                  <Alert
                    message="Deposit Payment"
                    description={`Please pay the deposit invoice (${approvalResult.invoice?.invoiceNumber}) before your scheduled appointment.`}
                    type="info"
                    showIcon
                  />
                )}
              </Space>
            }
            extra={[
              <Button type="primary" key="dashboard" onClick={() => navigate(portalConfig?.defaultLandingPage || '/dashboard')}>
                Go to Dashboard
              </Button>,
              <Button key="close" onClick={() => setScheduledJob(null)}>
                Close
              </Button>
            ]}
          />
        </Modal>
      )}
    </div>
  )
}

